import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiArrowLeft, FiCalendar, FiClock, FiDollarSign, FiFileText, FiGift, FiPrinter, FiDownload } from "react-icons/fi";
import html2pdf from "html2pdf.js";
import Cookies from "js-cookie";
import { graphqlRequest } from "../../../../utils/graphqlRequest";
import ContractorPayoutPortal from "./ContractorPayoutPortal";
import ContractorClaimsPanel from "./ContractorClaimsPanel";
import "../../styles/ContractorPayroll.css";

const GET_CONTRACTOR_PAYOUTS = `
  query ContractorPayouts($contractorIds: [String!], $periodKey: String) {
    contractorPayouts(contractorIds: $contractorIds, periodKey: $periodKey) {
      id
      contractorId
      periodKey
      reference
      totalPayable
      subtotal
      gst
      payType
      status
      payDate
      paidAt
    }
  }
`;

const SAVE_CONTRACTOR_PAYMENT_METHOD = `
  mutation SaveContractorPaymentMethod($contractorId: String!, $paymentMethod: String!) {
    saveContractorPaymentMethod(contractorId: $contractorId, paymentMethod: $paymentMethod)
  }
`;

const MARK_CONTRACTOR_PAYOUT_PAID = `
  mutation MarkContractorPayoutPaid($id: String!) {
    markContractorPayoutPaid(id: $id) {
      id
      contractorId
      periodKey
      status
      paidAt
    }
  }
`;

const GENERATE_CONTRACTOR_ABA = `
  mutation GenerateContractorAba($payoutIds: [String!]!) {
    generateContractorAba(payoutIds: $payoutIds) {
      id
      status
      totalCreditAmountCents
      employeeCount
    }
  }
`;

const GET_COMPANY_DETAILS = `
  query Company($id: String!) {
    company(id: $id) {
      companyName
      abn
      address
      suburb
      postCode
      state
    }
  }
`;

const GET_WORK_ENTRIES = `
  query ContractorWorkEntries(
    $startDate: String
    $endDate: String
  ) {
    contractorWorkEntries(
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      date
      status
      source
      workDescription
      contractorId
      projectId
      hours
      rate
      amount
      contractor {
        id
        firstName
        lastName
        payType
        perHourRate
        gstType
        businessName
        abnNumber
        bsb
        accountNumber
      }
      project {
        id
        name
      }
    }
  }
`;

const GET_WORK_TRACKING = `
  query ContractorWorkTracking(
    $startDate: String
    $endDate: String
  ) {
    contractorWorkTracking(
      startDate: $startDate
      endDate: $endDate
    ) {
      contractors {
        contractorId
        contractorName
        payType
        payRate
        shifts {
          id
          date
          duration
          hours
          approvalStatus
        }
      }
    }
  }
`;

const normalizePayType = (p) => String(p || "").toLowerCase().replace(/[_\s-]+/g, "-");
const normalizeGstType = (v) => String(v || "").trim().toLowerCase().replace(/[_\s-]+/g, "-");
const fmtCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;

// ─────────────────────────────────────────────────────────────────────────────
// PERIOD & PAYOUT LOGIC — pure functions, no UI
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert any Date → "YYYY-MM-DD" using LOCAL time (no UTC shift).
 * Safe for all timezones — never use toISOString() for date-only values.
 */
const toYMD = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Parse "YYYY-MM-DD" → local-midnight Date.
 * Appending T00:00:00 forces local interpretation, avoiding UTC offset issues.
 */
const parseYMD = (ymd) => new Date(`${ymd}T00:00:00`);

/**
 * Return the Monday that begins the ISO week containing `date`.
 * JS getDay(): 0 = Sunday, 1 = Monday … 6 = Saturday.
 */
const getMondayOf = (date) => {
  const d = new Date(date);
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return d;
};

/**
 * Calculate the exact period start date, end date, and unique key
 * for the selected period type.
 *
 * Weekly      → Monday of the selected week to Sunday   (7 days)
 * Fortnightly → Monday of the selected week to Sunday+7 (14 days)
 * Monthly     → 1st of the selected month to last day
 *
 * Returns:
 *   { startDate, endDate, key, label }  on success
 *   { error }                           on validation failure
 *
 * Key format examples:
 *   weekly-2026-03-02-2026-03-08
 *   fortnightly-2026-03-02-2026-03-15
 *   monthly-2026-03-01-2026-03-31
 */
const resolvePeriod = ({ periodType, selectedMonth, selectedDate }) => {
  if (periodType === "monthly") {
    if (!selectedMonth) return { error: "Please select a payout month." };
    const [y, m] = selectedMonth.split("-").map(Number);
    if (!y || !m || isNaN(y) || isNaN(m)) return { error: "Invalid month selected." };
    const start = new Date(y, m - 1, 1);
    const end   = new Date(y, m, 0);                    // last day of month
    const startDate = toYMD(start);
    const endDate   = toYMD(end);
    return {
      startDate,
      endDate,
      key: `monthly-${startDate}-${endDate}`,
      label: start.toLocaleDateString("en-AU", { month: "long", year: "numeric" }),
    };
  }

  if (!selectedDate) return { error: "Please select a payout date." };
  const base = parseYMD(selectedDate);
  if (isNaN(base.getTime())) return { error: "Invalid date selected." };

  if (periodType === "selected-date") {
    const ymd = toYMD(base);
    return {
      startDate: ymd,
      endDate: ymd,
      key: `date-${ymd}`,
      label: base.toLocaleDateString("en-AU"),
    };
  }

  // weekly = 7 days (Mon–Sun), fortnightly = 14 days (Mon–Sun+7)
  const monday  = getMondayOf(base);
  const end     = new Date(monday);
  end.setDate(monday.getDate() + (periodType === "fortnightly" ? 13 : 6));

  const startDate = toYMD(monday);
  const endDate   = toYMD(end);
  return {
    startDate,
    endDate,
    key: `${periodType}-${startDate}-${endDate}`,
    label: `${startDate} – ${endDate}`,
  };
};

/**
 * Filter timesheet rows (shifts or work entries) to only those
 * whose .date falls within [startDate, endDate] inclusive.
 *
 * ISO "YYYY-MM-DD" string comparison is safe, lexicographically correct,
 * and avoids any timezone-related Date parsing bugs.
 *
 * @param {Array<{date: string, hours: number}>} rows
 * @param {string|null|undefined} startDate  "YYYY-MM-DD"
 * @param {string|null|undefined} endDate    "YYYY-MM-DD"
 * @returns filtered rows (returns all rows when dates are absent)
 */
const filterByPeriod = (rows = [], startDate, endDate) => {
  if (!startDate || !endDate) return rows;
  return rows.filter((r) => r?.date && r.date >= startDate && r.date <= endDate);
};

/**
 * Sum the hours field across timesheet rows for the period.
 * @param {Array<{hours: number}>} rows
 * @returns {number}  rounded to 2 decimal places
 */
const sumHours = (rows = []) =>
  Number(rows.reduce((acc, r) => acc + Number(r?.hours || 0), 0).toFixed(2));

/**
 * Return the ISO start date of the NEXT period immediately after currentEndDate.
 *
 * Weekly / Fortnightly → next day, snapped to Monday (start of new ISO week).
 * Monthly              → next calendar day (new month's period begins there).
 *
 * @param {"weekly"|"fortnightly"|"monthly"} periodType
 * @param {string|null} currentEndDate  "YYYY-MM-DD"
 * @returns {string|null}  "YYYY-MM-DD"
 */
const getNextPeriodStart = (periodType, currentEndDate) => {
  if (!currentEndDate) return null;
  const next = parseYMD(currentEndDate);
  next.setDate(next.getDate() + 1);
  if (periodType === "weekly" || periodType === "fortnightly") {
    const dow = next.getDay();                          // snap to Monday
    if (dow !== 1) next.setDate(next.getDate() + (dow === 0 ? 1 : 8 - dow));
  }
  return toYMD(next);
};

/**
 * Return true if a paid payout already exists in the local status map
 * for the given period + row ID combination.
 *
 * Used to skip duplicate payout generation on the frontend before the
 * backend unique-constraint check fires.
 *
 * @param {Object} paymentStatusMap
 * @param {string} periodKey  e.g. "weekly-2026-03-02-2026-03-08"
 * @param {string} rowId      e.g. `${contractorId}-combined`
 */
const isAlreadyPaid = (paymentStatusMap, periodKey, rowId) =>
  paymentStatusMap[`${periodKey}-${rowId}`]?.paid === true;

/**
 * Format "YYYY-MM-DD" → "02 Mar 2026" for payment statement display.
 * Uses local Date (parseYMD) to avoid UTC day-shift on formatted output.
 * Returns null for invalid or missing input.
 */
const formatPeriodDate = (isoDate) => {
  if (!isoDate) return null;
  const d = parseYMD(isoDate);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Build the period range string shown on the payment statement.
 *
 * Input:  "2026-03-02", "2026-03-08"
 * Output: "02 Mar 2026 - 08 Mar 2026"   or   "—" when dates are missing.
 */
const formatPeriodRange = (startDate, endDate) => {
  const s = formatPeriodDate(startDate);
  const e = formatPeriodDate(endDate);
  return s && e ? `${s} - ${e}` : "—";
};

const PayoutTab = ({
  sharedUsers = [],
  sharedLoading = false,
  sharedError = "",
}) => {
  const [companyDetails, setCompanyDetails] = useState(null);
  const [periodType, setPeriodType] = useState("monthly");
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateError, setDateError] = useState("");
  const [payoutRows, setPayoutRows] = useState([]);
  const [activeStatement, setActiveStatement] = useState(null);
  const [portalRow, setPortalRow] = useState(null);
  // paymentStatusMap: { [rowId]: { paid, paidAt, backendId, periodKey } }
  const [paymentStatusMap, setPaymentStatusMap] = useState({});
  const [abaGeneratingId, setAbaGeneratingId] = useState(null);
  const [abaMessage, setAbaMessage] = useState(null); // { type: 'success'|'error', text }
  const [appliedPeriodKey, setAppliedPeriodKey] = useState("all");
  const [appliedPeriodLabel, setAppliedPeriodLabel] = useState("All Approved Entries");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paymentModes, setPaymentModes] = useState({}); // { [contractorId]: 'BANK' | 'CASH' }
  const [cashConfirmRow, setCashConfirmRow] = useState(null); // { contractorId, name }
  const statementRef = useRef(null);
  const period = useMemo(
    () => resolvePeriod({ periodType, selectedMonth, selectedDate }),
    [periodType, selectedMonth, selectedDate]
  );
  const getPaymentKey = (contractorId) => `${appliedPeriodKey}-${contractorId}`;

  useEffect(() => {
    const companyId = Cookies.get("companyId");
    if (!companyId) return;
    graphqlRequest(GET_COMPANY_DETAILS, { id: companyId })
      .then((data) => { if (data?.company) setCompanyDetails(data.company); })
      .catch(() => { });
  }, []);

  // Load existing payout status from backend for current rows
  const loadPayoutStatus = useCallback(async (rows, currentPeriodKey) => {
    if (!rows.length || currentPeriodKey === "all") return;
    try {
      const contractorIds = [...new Set(rows.map((r) => r.contractorId))];
      const data = await graphqlRequest(GET_CONTRACTOR_PAYOUTS, {
        contractorIds,
        periodKey: currentPeriodKey,
      });
      const records = data?.contractorPayouts || [];
      if (!records.length) return;
      setPaymentStatusMap((prev) => {
        const next = { ...prev };
        records.forEach((rec) => {
          // Key must match the row id format used everywhere else: `${contractorId}-combined`
          const rowId = `${rec.contractorId}-combined`;
          const key = `${currentPeriodKey}-${rowId}`;
          const isPaid      = String(rec.status || "").toUpperCase() === "PAID";
          const isAbaQueued = String(rec.status || "").toUpperCase() === "ABA_QUEUED";
          next[key] = {
            paid: isPaid,
            paidAt: isPaid ? rec.paidAt : null,
            backendId: rec.id,
            periodKey: currentPeriodKey,
            abaQueued: isAbaQueued,
          };
        });
        return next;
      });
    } catch {
      // non-blocking – status will remain as computed locally
    }
  }, []);

  const loadPayouts = async (applySelectedPeriod = false) => {
    if (applySelectedPeriod && period.error) {
      setDateError(period.error);
      return;
    }
    if (sharedLoading) {
      setError("Contractor details are still loading. Please try again.");
      return;
    }
    setLoading(true);
    setDateError("");
    setError(sharedError || "");
    try {
      const startDate = applySelectedPeriod ? period.startDate : undefined;
      const endDate = applySelectedPeriod ? period.endDate : undefined;
      setAppliedPeriodKey(applySelectedPeriod ? (period.key || "period") : "all");
      setAppliedPeriodLabel(applySelectedPeriod ? (period.label || "Selected Period") : "All Approved Entries");
      const [workEntriesResult, trackingResult] = await Promise.allSettled([
        graphqlRequest(GET_WORK_ENTRIES, {
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }),
        graphqlRequest(GET_WORK_TRACKING, {
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }),
      ]);
      if (workEntriesResult.status === "rejected") throw workEntriesResult.reason;
      const workEntriesData = workEntriesResult.value;
      const trackingData = trackingResult.status === "fulfilled" ? trackingResult.value : null;
      const approvedEntries = (workEntriesData?.contractorWorkEntries || []).filter(
        (entry) => String(entry?.status || "").toLowerCase() === "approved"
      );
      const trackingRows = trackingData?.contractorWorkTracking?.contractors || [];
      const trackingByContractor = trackingRows.reduce((acc, row) => {
        if (!row?.contractorId) return acc;
        acc[row.contractorId] = row;
        return acc;
      }, {});
      const usersById = new Map((sharedUsers || []).map((u) => [u.id, u]));
      const entriesByContractor = approvedEntries.reduce((acc, row) => {
        if (!row?.contractorId) return acc;
        if (!acc[row.contractorId]) acc[row.contractorId] = [];
        acc[row.contractorId].push(row);
        return acc;
      }, {});
      const contractorIds = new Set([
        ...Object.keys(entriesByContractor),
        ...Object.keys(trackingByContractor),
      ]);
      const rowsWithCards = Array.from(contractorIds).map((contractorId) => {
        const entries = entriesByContractor[contractorId] || [];
        const tracking = trackingByContractor[contractorId] || null;
        const firstEntryContractor = entries?.[0]?.contractor || {};
        const sharedUser = usersById.get(contractorId) || {};
        const user = {
          id: contractorId,
          firstName: sharedUser.firstName || firstEntryContractor.firstName || "",
          lastName: sharedUser.lastName || firstEntryContractor.lastName || "",
          businessName: sharedUser.businessName || firstEntryContractor.businessName || "Contractor Business",
          abnNumber: sharedUser.abnNumber || firstEntryContractor.abnNumber || "N/A",
          bsb: sharedUser.bsb || firstEntryContractor.bsb || "N/A",
          accountNumber: sharedUser.accountNumber || firstEntryContractor.accountNumber || "N/A",
          payType: sharedUser.payType || tracking?.payType || firstEntryContractor.payType || "",
          perHourRate: sharedUser.perHourRate ?? tracking?.payRate ?? firstEntryContractor.perHourRate ?? 0,
          gstType: sharedUser.gstType || firstEntryContractor.gstType || "",
          contractorPayDate: sharedUser.contractorPayDate || firstEntryContractor.contractorPayDate || null,
        };
        return { user, entries, tracking };
      });

      let runningIndex = 0;
      const payouts = rowsWithCards
        .map(({ user, entries, tracking }) => {
          const baseRate = Number(user.perHourRate || 0);
          const normalizedTimesheetPayType = normalizePayType(tracking?.payType || user.payType);
          const isTimesheetProjectPay = normalizedTimesheetPayType.includes("project");
          const gstType = normalizeGstType(user.gstType);
          // GST Exclusive = rate does NOT include GST → add 10% on top
          // GST Inclusive = rate already includes GST → no extra added
          // No gstType (not registered) → no GST at all
          const includesGst = gstType === "exclusive";

          // ── Approved timesheet shifts (filtered to selected period) ─────
          const periodEntries = filterByPeriod(entries, startDate, endDate);

          // Dates already covered by merged timesheet entries from contractorWorkEntries.
          // Skip tracking shifts for those dates to avoid double-counting the same work.
          const coveredTimesheetDates = new Set(
            periodEntries
              .filter((e) => String(e.source || "") === "timesheet")
              .map((e) => e.date)
              .filter(Boolean)
          );
          const allApprovedShifts = (tracking?.shifts || []).filter(
            (shift) =>
              String(shift?.approvalStatus || "").toUpperCase() === "APPROVED" &&
              shift?.date &&
              !coveredTimesheetDates.has(shift.date)
          );
          const approvedShifts = filterByPeriod(allApprovedShifts, startDate, endDate);
          const totalPeriodHours = sumHours([...approvedShifts, ...periodEntries]);

          // Derive actual working date range from the data (used when no period filter is applied)
          const allWorkDates = [
            ...approvedShifts.map((s) => s?.date),
            ...periodEntries.map((e) => e?.date),
          ].filter(Boolean).sort();
          const actualStartDate = startDate || allWorkDates[0] || null;
          const actualEndDate   = endDate   || allWorkDates[allWorkDates.length - 1] || null;

          // ── Hourly line items (timesheet + manual) ───────────────────────
          const timesheetHourlyItems = approvedShifts
            .filter(() => !isTimesheetProjectPay)
            .map((shift, idx) => {
              const hours = Number(shift?.hours || 0);
              const rate = Number(tracking?.payRate || baseRate || 0);
              return {
                id: `timesheet-${user.id}-${shift?.date || "shift"}-${idx}`,
                description: shift?.duration
                  ? `Timesheet (${shift.duration})`
                  : `Timesheet (${shift?.date || idx + 1})`,
                hours,
                rate,
                amount: Number((hours * rate).toFixed(2)),
              };
            });

          const manualHourlyItems = periodEntries
            .filter((entry) => Number(entry?.hours || 0) > 0)
            .map((entry, idx) => {
              const hours = Number(entry?.hours || 0);
              const rate = Number(entry?.rate ?? baseRate ?? 0);
              const amount = Number(entry?.amount ?? (hours * rate) ?? 0);
              return {
                id: entry?.id || `manual-hourly-${idx}`,
                description: entry?.workDescription
                  || entry?.project?.name
                  || `Work Entry (${entry?.date || idx + 1})`,
                hours,
                rate,
                amount: Number(amount.toFixed(2)),
              };
            });

          const hourlyLineItems = [...timesheetHourlyItems, ...manualHourlyItems];

          // ── Project line items (unique projects + timesheet project) ─────
          const uniqueProjects = new Map();
          periodEntries
            .filter((entry) => Number(entry?.hours || 0) <= 0)
            .forEach((entry) => {
              const key = entry.projectId || entry.project?.id || `entry-${entry.id}`;
              const entryRate = Number(entry.rate ?? 0);
              const rate = entryRate > 0 ? entryRate : Number(baseRate || 0);
              const amount = Number(entry?.amount ?? rate ?? 0);
              if (!uniqueProjects.has(key)) {
                uniqueProjects.set(key, {
                  id: key,
                  description: entry.project?.name || `Project ${uniqueProjects.size + 1}`,
                  rate,
                  amount: Number(amount.toFixed(2)),
                });
              } else {
                const prev = uniqueProjects.get(key);
                uniqueProjects.set(key, {
                  ...prev,
                  rate: rate > 0 ? rate : prev.rate,
                  amount: Number((Number(prev.amount) + Number(amount)).toFixed(2)),
                });
              }
            });

          const timesheetProjectItems = approvedShifts
            .filter(() => isTimesheetProjectPay)
            .map((shift, idx) => {
              const rate = Number(tracking?.payRate || baseRate || 0);
              return {
                id: `timesheet-project-${user.id}-${shift?.date || "shift"}-${idx}`,
                description: shift?.duration
                  ? `Timesheet Project (${shift.duration})`
                  : `Timesheet Project (${shift?.date || idx + 1})`,
                rate,
                amount: Number(rate.toFixed(2)),
              };
            });

          const projectLineItems = [...Array.from(uniqueProjects.values()), ...timesheetProjectItems];

          // Skip contractor if no work at all
          if (!hourlyLineItems.length && !projectLineItems.length) return null;

          // ── Skip if already paid for this period (duplicate prevention) ──
          const currentPeriodKey = applySelectedPeriod ? (period.key || "period") : "all";
          if (applySelectedPeriod && isAlreadyPaid(paymentStatusMap, currentPeriodKey, `${user.id}-combined`)) return null;


          // ── Combined totals ──────────────────────────────────────────────
          runningIndex += 1;
          const hourlySubtotal = Number(
            hourlyLineItems.reduce((s, i) => s + Number(i.amount || 0), 0).toFixed(2)
          );
          const projectSubtotal = Number(
            projectLineItems.reduce((s, i) => s + Number(i.amount || 0), 0).toFixed(2)
          );
          const subtotal = Number((hourlySubtotal + projectSubtotal).toFixed(2));
          const totalHours = Number(
            hourlyLineItems.reduce((s, i) => s + Number(i.hours || 0), 0).toFixed(2)
          );
          const gst = includesGst ? Number((subtotal * 0.1).toFixed(2)) : 0;
          const totalPayable = Number((subtotal + gst).toFixed(2));
          const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unnamed Contractor";
          const payTypeLabel = hourlyLineItems.length && projectLineItems.length
            ? "Hourly + Project"
            : hourlyLineItems.length ? "Hourly" : "Project";

          const sourceKeys = Array.from(new Set([
            ...periodEntries.filter((entry) => entry.id).map((entry) => `${entry.source === "timesheet" ? "timecard" : "work-entry"}:${entry.id}`),
            ...approvedShifts.filter((shift) => shift.id).map((shift) => `timecard:${shift.id}`),
          ]));

          return {
            id: `${user.id}-combined`,
            contractorId: user.id,
            reference: applySelectedPeriod
              ? `PAY-${periodType.toUpperCase()}-${String(startDate || "").replace(/-/g, "")}-${String(endDate || "").replace(/-/g, "")}-${String(runningIndex).padStart(3, "0")}`
              : `PAY-ALL-${String(runningIndex).padStart(3, "0")}`,
            fullName,
            initials: fullName.slice(0, 1).toUpperCase(),
            businessName: user.businessName || "Contractor Business",
            abnNumber: user.abnNumber || "N/A",
            bsb: user.bsb || "N/A",
            accountNumber: user.accountNumber || "N/A",
            payType: payTypeLabel,
            totalHours,
            totalProjects: projectLineItems.length,
            hourlyLineItems,
            projectLineItems,
            hourlySubtotal,
            projectSubtotal,
            subtotal,
            gst,
            includesGst,
            totalPayable,
            sourceKeys,
            contractorPayDate: user.contractorPayDate || null,
            totalPeriodHours,
            periodStartDate: actualStartDate,
            periodEndDate: actualEndDate,
            nextPeriodStart: getNextPeriodStart(periodType, actualEndDate),
          };
        })
        .filter(Boolean);

      setPayoutRows(payouts);

      // On preview load (no period applied): initialise selection to all + load saved payment modes
      if (!applySelectedPeriod) {
        setSelectedIds(new Set(payouts.map((r) => r.contractorId)));
        setPaymentModes((prev) => {
          const next = { ...prev };
          payouts.forEach((r) => {
            if (!next[r.contractorId]) {
              // Use saved DB value from sharedUsers, fall back to BANK
              const savedUser = (sharedUsers || []).find((u) => u.id === r.contractorId);
              next[r.contractorId] = savedUser?.contractorPaymentMethod || 'BANK';
            }
          });
          return next;
        });
      }

      if (applySelectedPeriod && payouts.length > 0) {
        // Period selection is a preview. Contractor claims create payable
        // records only after a finance reviewer approves their source lines.
        await loadPayoutStatus(payouts, period.key || "period");
      } else if (!applySelectedPeriod) {
        // For "all entries" view, just initialise pending status without backend call
        setPaymentStatusMap((prev) => {
          const next = { ...prev };
          payouts.forEach((row) => {
            const key = getPaymentKey(row.id);
            if (typeof next[key] === "undefined") {
              next[key] = { paid: false, paidAt: null };
            }
          });
          return next;
        });
      }
    } catch (err) {
      const rawMessage = String(err?.message || "");
      const isConnectivityIssue =
        rawMessage.toLowerCase().includes("no response from server") ||
        rawMessage.toLowerCase().includes("network error") ||
        rawMessage.toLowerCase().includes("timeout");
      setError(
        isConnectivityIssue
          ? "Unable to load payout data right now. Connection to backend is unstable. Please try again."
          : (err?.message || "Failed to fetch payout details.")
      );
      setPayoutRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPayoutsRef = useRef(loadPayouts);
  // Keep ref in sync without creating a hook dependency edge that changes every render.
  loadPayoutsRef.current = loadPayouts;

  useEffect(() => {
    if (activeStatement) return;
    loadPayoutsRef.current(false);
  }, [activeStatement, sharedLoading, sharedError, sharedUsers]);

  // When rows change and we have a period, sync status from backend
  useEffect(() => {
    if (payoutRows.length && appliedPeriodKey !== "all") {
      loadPayoutStatus(payoutRows, appliedPeriodKey);
    }
  }, [payoutRows, appliedPeriodKey, loadPayoutStatus]);

  const metrics = useMemo(() => {
    const totalContractors = payoutRows.length;
    const totalHours = Number(payoutRows.reduce((sum, row) => sum + row.totalHours, 0).toFixed(2));
    const totalProjects = payoutRows.reduce((sum, row) => sum + (row.projectLineItems?.length || 0), 0);
    const totalPayout = Number(payoutRows.reduce((sum, row) => sum + row.totalPayable, 0).toFixed(2));
    return { totalContractors, totalHours, totalProjects, totalPayout };
  }, [payoutRows]);

  const activePayment = activeStatement ? paymentStatusMap[getPaymentKey(activeStatement.id)] : null;
  const activePaymentDate = activePayment?.paidAt
    ? new Date(activePayment.paidAt).toLocaleDateString("en-AU")
    : "—";

  const handleDownloadPdf = async () => {
    if (!activeStatement || !statementRef.current) return;
    try {
      const safeName = String(activeStatement.fullName || "contractor")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();
      await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename: `${activeStatement.reference}-${safeName}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(statementRef.current)
        .save();
    } catch (pdfErr) {
      setError(pdfErr?.message || "Unable to download PDF. Please try again.");
    }
  };

  // ── Payment mode (BANK / CASH) ─────────────────────────────────────────────
  const handlePaymentModeChange = (contractorId, mode, name) => {
    setPaymentModes((prev) => ({ ...prev, [contractorId]: mode }));
    if (mode === 'CASH') {
      setCashConfirmRow({ contractorId, name });
    } else {
      // Persist BANK immediately (no confirmation needed)
      graphqlRequest(SAVE_CONTRACTOR_PAYMENT_METHOD, { contractorId, paymentMethod: 'BANK' }).catch(() => {});
    }
  };

  const confirmCashPayment = (contractorId) => {
    setCashConfirmRow(null);
    graphqlRequest(SAVE_CONTRACTOR_PAYMENT_METHOD, { contractorId, paymentMethod: 'CASH' }).catch(() => {});
  };

  const cancelCashPayment = (contractorId) => {
    setPaymentModes((p) => ({ ...p, [contractorId]: 'BANK' }));
    setCashConfirmRow(null);
    graphqlRequest(SAVE_CONTRACTOR_PAYMENT_METHOD, { contractorId, paymentMethod: 'BANK' }).catch(() => {});
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const allSelected = payoutRows.length > 0 && selectedIds.size === payoutRows.length;
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(payoutRows.map((r) => r.contractorId)));
  };
  const toggleSelect = (contractorId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(contractorId)) next.delete(contractorId);
      else next.add(contractorId);
      return next;
    });
  };

  if (portalRow) {
    const statusKey = getPaymentKey(portalRow.id);
    const status = paymentStatusMap[statusKey];
    const isPaid = typeof status === "boolean" ? status : !!status?.paid;
    return (
      <ContractorPayoutPortal
        row={portalRow}
        periodLabel={appliedPeriodLabel}
        isPaid={isPaid}
        paidAt={paymentStatusMap[statusKey]?.paidAt}
        paymentMethod={paymentModes[portalRow.contractorId] || 'BANK'}
        onClose={() => setPortalRow(null)}
      />
    );
  }

  if (activeStatement) {
    return (
      <div className="payout-statement-page">
        <div className="payout-statement-header">
          <div className="payout-statement-left">
            <button
              type="button"
              className="payout-back-icon-btn"
              onClick={() => setActiveStatement(null)}
              title="Back to payout list"
            >
              <FiArrowLeft size={15} />
            </button>
            <h3>Work Estimate</h3>
          </div>
          <div className="payout-statement-actions">
            <button type="button" className="payout-ghost-btn" onClick={() => window.print()}>
              <FiPrinter size={14} />
              Print
            </button>
            <button type="button" className="payout-primary-btn" onClick={handleDownloadPdf}>
              <FiDownload size={14} />
              Download PDF
            </button>
          </div>
        </div>

        <div className="payout-statement-card" ref={statementRef}>
          <div className="payout-company-row">
            <div>
              <div className="payout-company-logo">
                {companyDetails?.logo ? (
                  <img
                    src={companyDetails.logo}
                    alt={companyDetails.companyName}
                    className="company-logo-img"
                  />
                ) : (
                  <span>{companyDetails?.companyName}</span>
                )}
              </div>
              <div className="payout-company-name">
                {companyDetails?.companyName || Cookies.get("companyName") || "—"}
              </div>
              <div className="payout-company-meta">
                <strong>ABN:</strong> {companyDetails?.abn || "—"}
              </div>
              <div className="payout-company-meta">
                {" "}
                {[companyDetails?.address, companyDetails?.suburb, companyDetails?.state, companyDetails?.postCode]
                  .filter(Boolean)
                  .join(", ") || "Address: —"}
              </div>
            </div>
            <div className="payout-statement-meta">
              <div className="meta-title">Payment Statement</div>
              <div className="meta-row">
                Period:{" "}
                {formatPeriodRange(activeStatement?.periodStartDate, activeStatement?.periodEndDate)}
              </div>
              <div className="meta-row">Reference: {activeStatement.reference}</div>
            </div>
          </div>

          <div className="payout-contractor-block">
            <div className="label">CONTRACTOR DETAILS</div>
            <div className="value">{activeStatement.fullName}</div>
            <div className="meta">ABN: {activeStatement.abnNumber}</div>
            <div className="meta">BSB: {activeStatement.bsb} | Account: {activeStatement.accountNumber}</div>
            <div className="meta" style={{ marginTop: 4 }}>
              Payment Mode:{" "}
              <span style={{
                display: "inline-block",
                padding: "1px 8px",
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 700,
                background: paymentModes[activeStatement.contractorId] === "CASH" ? "#fef9c3" : "#dbeafe",
                color: paymentModes[activeStatement.contractorId] === "CASH" ? "#92400e" : "#1d4ed8",
              }}>
                {paymentModes[activeStatement.contractorId] === "CASH" ? "Cash" : "Bank Transfer"}
              </span>
            </div>
          </div>

          {/* Hourly Work Section */}
          {(activeStatement.hourlyLineItems || []).length > 0 && (
            <>
              <div className="payout-section-heading">Hourly Work</div>
              <table className="payout-statement-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Hours</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {activeStatement.hourlyLineItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td>{item.hours ? `${item.hours}h` : "—"}</td>
                      <td className="right">{fmtCurrency(item.rate)}/hr</td>
                      <td className="right">{fmtCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  <tr className="payout-section-subtotal">
                    <td colSpan="3"><strong>Hourly Subtotal</strong></td>
                    <td className="right"><strong>{fmtCurrency(activeStatement.hourlySubtotal)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* Project Work Section */}
          {(activeStatement.projectLineItems || []).length > 0 && (
            <>
              <div className="payout-section-heading">Project Work</div>
              <table className="payout-statement-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {activeStatement.projectLineItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td>1</td>
                      <td className="right">{fmtCurrency(item.rate)}</td>
                      <td className="right">{fmtCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  <tr className="payout-section-subtotal">
                    <td colSpan="3"><strong>Project Subtotal</strong></td>
                    <td className="right"><strong>{fmtCurrency(activeStatement.projectSubtotal)}</strong></td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          <div className="payout-gst-grid">
            <div className="gst-head">GST REGISTERED (10%)</div>
            <div className="gst-col-head">Percent</div>
            <div className="gst-col-head">Amount</div>
            <div className="gst-row-label">{activeStatement.includesGst ? "GST Exclusive" : "GST Inclusive"}</div>
            <div className="gst-row-percent">{activeStatement.includesGst ? "10%" : "0%"}</div>
            <div className="gst-row-amount">{fmtCurrency(activeStatement.gst)}</div>
          </div>

          <div className="payout-totals">
            <div>
              <span>Subtotal</span>
              <strong>{fmtCurrency(activeStatement.subtotal)}</strong>
            </div>
            <div className="big">
              <span>Total Payable</span>
              <strong>{fmtCurrency(activeStatement.totalPayable)}</strong>
            </div>
          </div>

          <div className="payout-statement-footer">
            <div>
              <div className="tiny-label">PAYMENT DATE</div>
              <div className="tiny-value">{activePaymentDate}</div>
            </div>
            <div>
              <div className="tiny-label">REFERENCE ID</div>
              <div className="tiny-value">{activeStatement.reference}</div>
            </div>
          </div>

          <div className="payout-statement-note">
            This is a payment statement for contractor services. No tax or superannuation deductions apply.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payout-tab">
      <ContractorClaimsPanel />
      <p>Work below is an estimate for review. A payable payout is created when a contractor claim is approved above.</p>
      <div className="contractor-payroll-toolbar">
        <div className="toolbar-title">
          <h2>Contractor Payout</h2>
          <p>Generate and manage contractor payments</p>
        </div>
      </div>

      {/* ABA message banner */}
      {abaMessage && (
        <div style={{
          background: abaMessage.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${abaMessage.type === 'success' ? '#86efac' : '#fecaca'}`,
          color: abaMessage.type === 'success' ? '#15803d' : '#dc2626',
          borderRadius: 8,
          padding: '10px 14px',
          margin: '0 0 12px 0',
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{abaMessage.text}</span>
          <button
            onClick={() => setAbaMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1,
              color: abaMessage.type === 'success' ? '#15803d' : '#dc2626' }}
          >×</button>
        </div>
      )}

      <div className="payout-filter-card">
        <div className="payout-period-input">
          <FiCalendar size={16} />
          <div>
            <label>Select Period</label>
            <select
              value={periodType}
              onChange={(e) => {
                setPeriodType(e.target.value);
                setActiveStatement(null);
                setDateError("");
              }}
            >
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly</option>
              <option value="selected-date">Selected Date</option>
            </select>
          </div>
          <div>
            <label>
              {periodType === "monthly"
                ? "Month"
                : (periodType === "selected-date" ? "Selected Date" : "Anchor Date")}
            </label>
            {periodType === "monthly" ? (
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setActiveStatement(null);
                  setDateError("");
                }}
              />
            ) : (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setActiveStatement(null);
                  setDateError("");
                }}
              />
            )}
          </div>
        </div>
        <button
          type="button"
          className="payout-primary-btn"
          disabled={loading || sharedLoading}
          onClick={() => loadPayouts(true)}
        >
          View Period
        </button>
      </div>
      {!!dateError && <div className="contractor-payroll-state error">{dateError}</div>}

      <div className="payout-summary-grid">
        <div className="payout-summary-card">
          <div className="icon blue"><FiGift size={16} /></div>
          <div>
            <div className="label">Contractors</div>
            <div className="value">{metrics.totalContractors}</div>
          </div>
        </div>
        <div className="payout-summary-card">
          <div className="icon green"><FiClock size={16} /></div>
          <div>
            <div className="label">Total Hours</div>
            <div className="value">{metrics.totalHours}</div>
          </div>
        </div>
        <div className="payout-summary-card">
          <div className="icon purple"><FiGift size={16} /></div>
          <div>
            <div className="label">Total Projects</div>
            <div className="value">{metrics.totalProjects}</div>
          </div>
        </div>
        <div className="payout-summary-card">
          <div className="icon orange"><FiDollarSign size={16} /></div>
          <div>
            <div className="label">Total Payout</div>
            <div className="value">{fmtCurrency(metrics.totalPayout)}</div>
          </div>
        </div>
      </div>

      {loading && <div className="contractor-payroll-state">Loading payout details...</div>}
      {!loading && error && <div className="contractor-payroll-state error">{error}</div>}

      {!loading && !error && (
        <div className="payout-table-wrap">
          <table className="payout-table">
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    title="Select all"
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th>Contractor</th>
                <th>Pay Type</th>
                <th>Hours</th>
                <th>Projects</th>
                <th>Subtotal</th>
                <th>Total Payable</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payoutRows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-cell">No payout records found for this period.</td>
                </tr>
              ) : (
                payoutRows.map((row) => {
                  const statusKey   = getPaymentKey(row.id);
                  const status      = paymentStatusMap[statusKey];
                  const isPaid      = typeof status === "boolean" ? status : !!status?.paid;
                  const isAbaQueued = !!status?.abaQueued;
                  const backendId   = status?.backendId;
                  const isCash      = paymentModes[row.contractorId] === 'CASH';
                  const isSelected  = selectedIds.has(row.contractorId);
                  return (
                    <tr
                      key={row.id}
                      style={{
                        background: isCash ? '#fffbeb' : undefined,
                        opacity: isSelected ? 1 : 0.5,
                      }}
                    >
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(row.contractorId)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="cpp-name-link"
                          onClick={() => setPortalRow(row)}
                        >
                          {row.fullName}
                        </button>
                      </td>
                      <td>
                        <span className={`rate-pill ${row.payType === "Project" ? "project" : ""}`}>{row.payType}</span>
                      </td>
                      <td>{row.totalHours ? `${row.totalHours}h` : "—"}</td>
                      <td>{row.totalProjects || "—"}</td>
                      <td>{fmtCurrency(row.subtotal)}</td>
                      <td className="strong">{fmtCurrency(row.totalPayable)}</td>
                      <td>
                        {isPaid || isAbaQueued ? (
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                            background: isCash ? '#fef9c3' : '#dbeafe',
                            color: isCash ? '#92400e' : '#1d4ed8',
                          }}>
                            {isCash ? 'CASH' : 'BANK'}
                          </span>
                        ) : (
                          <select
                            value={paymentModes[row.contractorId] || 'BANK'}
                            onChange={(e) => handlePaymentModeChange(row.contractorId, e.target.value, row.fullName)}
                            style={{
                              padding: "3px 6px", borderRadius: 4, border: "1px solid #d1d5db",
                              fontSize: 11, fontWeight: 600, cursor: "pointer",
                              background: isCash ? '#fef9c3' : '#f0fdf4',
                              color: isCash ? '#92400e' : '#15803d',
                            }}
                          >
                            <option value="BANK">BANK</option>
                            <option value="CASH">CASH</option>
                          </select>
                        )}
                      </td>
                      <td>
                        {isPaid ? (
                          <span className="status-pill paid">Paid</span>
                        ) : isAbaQueued ? (
                          <span className="status-pill" style={{ background: "#dbeafe", color: "#1d4ed8" }}>ABA Queued</span>
                        ) : !backendId ? (
                          <span className="status-pill pending">See claims above</span>
                        ) : (
                          <span className="status-pill pending">Pending</span>
                        )}
                      </td>
                      <td>
                        <div className="payout-actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="icon-btn"
                            title="View payout statement"
                            onClick={() => setActiveStatement(row)}
                          >
                            <FiFileText size={14} />
                          </button>
                          <button
                            type="button"
                            className="mark-paid-btn"
                            disabled={isPaid || isAbaQueued || !backendId}
                            onClick={async () => {
                              try {
                                const result = await graphqlRequest(MARK_CONTRACTOR_PAYOUT_PAID, { id: backendId });
                                const paidAt = result?.markContractorPayoutPaid?.paidAt;
                                await loadPayoutStatus(payoutRows, appliedPeriodKey);
                                setAbaMessage({ type: "success", text: `Payment recorded${paidAt ? ` on ${new Date(paidAt).toLocaleDateString("en-AU")}` : ""}.` });
                              } catch (e) {
                                setAbaMessage({ type: "error", text: e?.message || "Payment could not be recorded." });
                              }
                            }}
                          >
                            {isPaid ? "Paid" : "Mark Paid"}
                          </button>
                          {/* ABA button: only shown when PENDING, not yet queued, and BANK payment */}
                          {!isPaid && !isAbaQueued && !isCash && backendId && (
                            <button
                              type="button"
                              className="mark-paid-btn"
                              style={{ background: "#dbeafe", color: "#1d4ed8", marginLeft: 4 }}
                              disabled={abaGeneratingId === backendId}
                              title="Generate ABA Direct Entry file for this payout"
                              onClick={async () => {
                                setAbaGeneratingId(backendId);
                                setAbaMessage(null);
                                try {
                                  const data = await graphqlRequest(GENERATE_CONTRACTOR_ABA, { payoutIds: [backendId] });
                                  const count = data?.generateContractorAba?.employeeCount ?? 1;
                                  setAbaMessage({
                                    type: "success",
                                    text: `ABA file created for ${count} payout(s). Go to ABA Pay Runs tab to approve and download.`,
                                  });
                                  setPaymentStatusMap((prev) => ({
                                    ...prev,
                                    [statusKey]: { ...prev[statusKey], abaQueued: true },
                                  }));
                                } catch (e) {
                                  setAbaMessage({ type: "error", text: e.message || "ABA generation failed." });
                                } finally {
                                  setAbaGeneratingId(null);
                                }
                              }}
                            >
                              {abaGeneratingId === backendId ? "…" : "ABA"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Cash Payment Confirmation Modal */}
      {cashConfirmRow && (
        <div className="tt-overlay" onClick={() => cancelCashPayment(cashConfirmRow.contractorId)}>
          <div className="tt-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tt-confirm-icon" style={{ background: '#fffbeb', color: '#92400e' }}>
              <FiDollarSign size={20} />
            </div>
            <h3 className="tt-confirm-title">Cash Payment Selected</h3>
            <p className="tt-confirm-desc">
              <strong>{cashConfirmRow.name}</strong> will be paid in cash.
              No ABA bank transfer file will be generated for this contractor.
            </p>
            <p className="tt-confirm-warning">
              Cash payments are recorded in the system but are not sent to the bank electronically. Ensure payment is made manually.
            </p>
            <div className="tt-confirm-actions">
              <button
                className="tt-confirm-btn cancel"
                onClick={() => cancelCashPayment(cashConfirmRow.contractorId)}
              >
                Cancel
              </button>
              <button
                className="tt-confirm-btn proceed"
                style={{ background: '#92400e' }}
                onClick={() => confirmCashPayment(cashConfirmRow.contractorId)}
              >
                Confirm Cash
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default PayoutTab;
